import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Building2, Pencil, Landmark, Upload, Lock, Check, X, Camera } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

function toBase64(file) {
  return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
}

function BankingRequestDialog({ profile, onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ bank_name: "", bank_account: "", note: "", document: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm({ bank_name: profile.bank_name || "", bank_account: profile.bank_account || "", note: "", document: "" });
  }, [open, profile]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.bank_name || !form.bank_account) return toast.error("Enter the bank and account number");
    if (!form.document) return toast.error("Please upload proof of bank account");
    setBusy(true);
    try {
      await api.post("/worker/change-request", form);
      toast.success("Banking update sent to admin for approval");
      setOpen(false); onSubmitted();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5 rounded-full" data-testid="banking-update-button"><Upload className="h-3.5 w-3.5" /> Submit update to admin</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Update banking details</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <p className="rounded-lg bg-secondary px-3 py-2 text-xs text-muted-foreground">For your security, banking changes are verified by admin before they take effect.</p>
          <div><Label>Bank name</Label><Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} data-testid="bank-req-bankname" className="mt-1" placeholder="e.g. Capitec" /></div>
          <div><Label>Account number</Label><Input value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} data-testid="bank-req-account" className="mt-1" /></div>
          <div>
            <Label>Proof of bank account (required)</Label>
            <label className="mt-1 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground" data-testid="bank-req-upload">
              <Upload className="h-4 w-4" /> {form.document ? "Document attached ✓" : "Upload bank letter / statement"}
              <input type="file" accept="image/*,.pdf" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (f) { const doc = await toBase64(f); setForm((s) => ({ ...s, document: doc })); } }} />
            </label>
          </div>
          <div><Label>Note (optional)</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} className="mt-1" /></div>
          <DialogFooter><Button type="submit" disabled={busy} data-testid="bank-req-submit" className="rounded-full bg-brand-navy font-semibold text-white hover:bg-brand-navy/90">{busy ? "Sending…" : "Submit to admin"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function WorkerProfile() {
  const [p, setP] = useState(null);
  const [requests, setRequests] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", place_of_work: "", photo: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/worker/profile").then((r) => {
      setP(r.data);
      setForm({ name: r.data.name || "", phone: r.data.phone || "", place_of_work: r.data.place_of_work || "", photo: r.data.photo || "" });
    }).catch(() => {});
    api.get("/worker/change-requests").then((r) => setRequests(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/worker/profile", form);
      setP(data); setEditing(false);
      toast.success("Details updated");
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  if (!p) return null;
  const pendingBank = requests.find((r) => r.status === "pending" && (r.bank_name || r.bank_account));

  return (
    <div className="space-y-4 animate-fade-up" data-testid="worker-profile">
      <div className="flex items-center gap-4 rounded-2xl border border-border/50 bg-card p-5 shadow-sm">
        {p.photo ? <img src={p.photo} alt={p.name} className="h-20 w-20 rounded-2xl object-cover ring-2 ring-primary/20" /> :
          <div className="grid h-20 w-20 place-items-center rounded-2xl bg-primary/10 font-heading text-2xl font-bold text-primary">{p.name?.[0]}</div>}
        <div>
          <p className="font-heading text-xl font-bold">{p.name}</p>
          <p className="text-sm text-muted-foreground">{p.email}</p>
          <p className="mt-1 flex items-center gap-1 text-sm font-semibold text-brand-cyan"><Star className="h-4 w-4 fill-brand-cyan" /> {p.rating_avg || "New"} ({p.rating_count})</p>
        </div>
      </div>

      {p.workplace_name && (
        <div className="flex items-center gap-3 rounded-2xl border border-border/50 bg-card p-4 shadow-sm" data-testid="profile-workplace">
          {p.workplace_logo ? <img src={p.workplace_logo} alt="" className="h-10 w-10 rounded-lg object-cover" /> : <Building2 className="h-6 w-6 text-primary" />}
          <div><p className="overline-label text-muted-foreground">Workplace</p><p className="font-semibold">{p.workplace_name}</p></div>
        </div>
      )}

      {/* Personal details — editable */}
      <div className="rounded-2xl border border-border/50 bg-card p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-bold">Personal details</h2>
          {!editing && (
            <Button variant="ghost" size="icon" onClick={() => setEditing(true)} data-testid="edit-personal-icon" className="rounded-full text-primary" aria-label="Edit details">
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>

        {!editing ? (
          <div className="mt-3 space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Name</span><span className="font-semibold">{p.name || "—"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Phone</span><span className="font-semibold" data-testid="profile-phone-value">{p.phone || "—"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Place of work</span><span className="font-semibold text-right">{p.place_of_work || "—"}</span></div>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <div className="flex items-center gap-3">
              {form.photo ? <img src={form.photo} alt="" className="h-14 w-14 rounded-xl object-cover" /> : <div className="grid h-14 w-14 place-items-center rounded-xl bg-secondary text-muted-foreground"><Camera className="h-5 w-5" /></div>}
              <label className="cursor-pointer text-sm font-semibold text-primary" data-testid="profile-photo-change">
                Change photo
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (f) { const d = await toBase64(f); setForm((s) => ({ ...s, photo: d })); } }} />
              </label>
            </div>
            <div><Label htmlFor="name">Name</Label><Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name-input" className="mt-1" /></div>
            <div><Label htmlFor="phone">Phone number</Label><Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone-input" className="mt-1" placeholder="+27 …" /></div>
            <div><Label htmlFor="pow">Place of work</Label><Input id="pow" value={form.place_of_work} onChange={(e) => setForm({ ...form, place_of_work: e.target.value })} data-testid="profile-pow-input" className="mt-1" /></div>
            <div className="flex gap-2 pt-1">
              <Button onClick={save} disabled={busy} data-testid="profile-save-button" className="flex-1 gap-1.5 rounded-full bg-brand-navy font-semibold text-white hover:bg-brand-navy/90"><Check className="h-4 w-4" /> {busy ? "Saving…" : "Save"}</Button>
              <Button variant="outline" onClick={() => { setEditing(false); setForm({ name: p.name || "", phone: p.phone || "", place_of_work: p.place_of_work || "", photo: p.photo || "" }); }} data-testid="profile-cancel-button" className="gap-1.5 rounded-full"><X className="h-4 w-4" /> Cancel</Button>
            </div>
          </div>
        )}
      </div>

      {/* Banking details — read-only, admin-approved changes */}
      <div className="rounded-2xl border border-border/50 bg-card p-5 shadow-sm" data-testid="banking-details">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-1.5 font-heading font-bold"><Lock className="h-4 w-4 text-muted-foreground" /> Banking details</h2>
          <BankingRequestDialog profile={p} onSubmitted={load} />
        </div>
        <div className="mt-3 space-y-3 text-sm">
          <div className="flex items-center justify-between"><span className="text-muted-foreground">Bank</span><span className="font-semibold">{p.bank_name || "—"}</span></div>
          <div className="flex items-center justify-between"><span className="text-muted-foreground">Account</span><span className="font-semibold">{p.bank_account || "—"}</span></div>
        </div>
        <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground"><Landmark className="h-3.5 w-3.5" /> Banking details can only be changed with admin approval and proof of account.</p>
        {pendingBank && <p className="mt-2 rounded-lg bg-brand-orange/10 px-3 py-2 text-xs font-medium text-brand-orange" data-testid="banking-pending"><Badge className="mr-1 border-0 bg-brand-orange/20 text-brand-orange">pending</Badge> Banking update awaiting admin approval.</p>}
      </div>
    </div>
  );
}
